import { Component, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Environment, EnvironmentService, SessionService } from '@galaxy/core';
import { AccountRouteParams } from '../../core/routeparams';
import { IAMTokenService } from '../../core/iam-token.service';

interface ExchangeResponse {
  token: string;
}

// Unguarded landing for partner (LocalEdge) SSO. LocalEdge links here with a
// signed JWT in `token`. We exchange it with wp-cloud-manager for a Vendasta
// session and establish it on this domain ourselves, then hand off to the
// guarded /entry route. Setting the session here — rather than having
// wp-cloud-manager Set-Cookie for the dashboard domain — avoids the
// cross-domain cookie the browser would drop.
@Component({
  selector: 'app-partner-sso',
  template: ``,
  standalone: false,
})
export class PartnerSsoComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly iamToken = inject(IAMTokenService);
  private readonly session = inject(SessionService);
  private readonly environment = inject(EnvironmentService);

  ngOnInit(): void {
    const accountId = (this.route.snapshot.params as AccountRouteParams).accountGroupId;
    const token = this.route.snapshot.queryParamMap.get('token');

    if (!token) {
      this.router.navigate(['/entry', accountId]);
      return;
    }

    this.http.post<ExchangeResponse>(this.exchangeUrl(), { token }).subscribe({
      next: (resp) => {
        // The account guard pings same-origin /api/v2/account/ which reads the
        // iam_token cookie; cross-origin gateway calls read SessionService.
        // Establish both from the one minted session.
        this.iamToken.setIAMToken(resp.token);
        this.session.setSessionId(resp.token);
        this.router.navigate(['/entry', accountId]);
      },
      error: () => {
        this.router.navigate(['/app-insufficient-permission', accountId]);
      },
    });
  }

  private exchangeUrl(): string {
    return `https://${this.wpCloudManagerHost()}/wp_cloud_manager/partner-sso/exchange`;
  }

  private wpCloudManagerHost(): string {
    return this.environment.getEnvironment() === Environment.PROD
      ? 'wp-cloud-manager-prod.apigateway.co'
      : 'wp-cloud-manager-demo.apigateway.co';
  }
}
